import { useEffect, useRef, useState } from "react";
import { InquiryShell } from "./InquiryShell";
import { apiFetch } from "@shared/api-client";
import { track } from "@/lib/analytics";
import { type InquiryProfileConfig, quoteIsDrafted } from "./inquiry-profile-schema";
import styles from "./InquirySuccess.module.css";

/**
 * Post-submit confirmation with live quote status. With a statusToken it
 * polls GET /api/inquiry/status — 10s cadence for the first 2 minutes,
 * then 30s, stopping entirely at 15 minutes; a window-focus refetch keeps
 * a backgrounded tab honest. For a Company intake does not quote for
 * (`quoteIsDrafted` false) there is no quote to wait on, and it renders the
 * static thank-you.
 *
 * States: polling ("<Company> is preparing your quote and will email it to
 * you shortly.") → ready ("Your quote is ready"). Each line names the
 * Company when the caller passes one, and falls back to the first person
 * when it doesn't.
 *
 * There is no "view it now" link. The screen once carried one, from the era
 * when the AI draft went straight to the Customer and "ready" arrived in
 * about thirty seconds. An Operator now reviews and sends every quote, so
 * the sent state lands hours or days later — long past the 15-minute poll
 * window, on a tab nobody left open. Email is the delivery channel; this
 * screen only says the request landed.
 *
 * The pending line is deliberately true for the whole wait rather than
 * being swapped for "still working on it" after ~90s. This screen is a
 * heading and one sentence on an otherwise empty page, so that sentence IS
 * the screen — rewriting it under the reader looked like the page changing
 * its mind, not a status update. Only a real state change (the quote
 * becoming ready) may change the copy.
 */

interface StatusResponse {
  status: "received" | "quote_ready";
}

const FAST_POLL_MS = 10_000;
const SLOW_POLL_MS = 30_000;
const FAST_WINDOW_MS = 2 * 60_000;
const STOP_AFTER_MS = 15 * 60_000;

function useInquiryStatus(
  statusToken: string | undefined,
): StatusResponse["status"] {
  const [state, setState] = useState<StatusResponse>({ status: "received" });
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!statusToken) return;
    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const res = await apiFetch<StatusResponse>(
          `/api/inquiry/status?token=${encodeURIComponent(statusToken)}`,
        );
        if (cancelled) return;
        setState(res);
        if (res.status !== "received") return; // terminal for this screen
      } catch {
        /* transient — keep polling */
      }
      schedule();
    };

    const schedule = (): void => {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= STOP_AFTER_MS) return;
      const delay = elapsed < FAST_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;
      timer = setTimeout(() => void poll(), delay);
    };

    const onFocus = (): void => {
      if (stateRef.current.status === "received") void poll();
    };

    window.addEventListener("focus", onFocus);
    schedule();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [statusToken]);

  return state.status;
}

export function InquirySuccess({
  profile,
  path,
  statusToken: submittedToken,
  title,
  staticSubtitle,
}: {
  profile: Pick<InquiryProfileConfig, "key" | "quoting" | "company">;
  path: "full_brief" | "quick_enquiry" | "dry_hire";
  /** What the submission returned; polled only where a quote is drafted. */
  statusToken?: string;
  title: string;
  /** Shown when there is no quote to wait on. */
  staticSubtitle: string;
}): React.JSX.Element {
  const profileKey = profile.key;
  const statusToken = quoteIsDrafted(profile) ? submittedToken : undefined;
  // Keeps the company header bar on after submitting, as on every step.
  const company = profile.company
    ? { name: profile.company.name, url: profile.company.publicUrl }
    : undefined;
  const status = useInquiryStatus(statusToken);
  const quoteReadyTrackedRef = useRef(false);

  useEffect(() => {
    if (status === "quote_ready" && !quoteReadyTrackedRef.current) {
      quoteReadyTrackedRef.current = true;
      track("quote_ready_shown", { profileKey, path });
    }
  }, [status, profileKey, path]);

  let heading = title;
  let body: React.ReactNode = staticSubtitle;

  if (statusToken) {
    // Named where we can: "Preparing your quote…" reads like a system
    // spinner, and the Customer is waiting on a company, not on us.
    const who = company?.name;
    if (status === "quote_ready") {
      heading = "Your quote is ready";
      body = who
        ? `${who} will email it to you shortly.`
        : "It will be emailed to you shortly.";
    } else {
      body = who
        ? `${who} is preparing your quote and will email it to you shortly.`
        : "Your quote is being prepared and will be emailed to you shortly.";
    }
  }

  return (
    <InquiryShell company={company}>
      <div className={styles.screen}>
        <h1 className={styles.heading}>{heading}</h1>
        <p className={styles.body}>{body}</p>
      </div>
    </InquiryShell>
  );
}
